import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Package, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CardContent, CardFooter, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { AuthService } from '@/services/core/auth'
import { AxProductsApi, type AxProduct } from '@/services/earnings/axProductsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { ProductTable } from '../../components/ProductTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { FilterField } from '../../components/FilterButton'
import { ProductTypeFilter, type ProductTypeFilterValue } from '../../components/ProductTypeFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { useUrlSearch } from '@/hooks/useUrlSearch'

export function ProductsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const hasToken = AuthService.isAuthenticated()

  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>(
    'filters.products.status',
    'ACTIVE'
  )
  const [typeFilter, setTypeFilter] = usePersistedState<ProductTypeFilterValue>(
    'filters.products.type',
    'all'
  )
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<AxProduct[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['axproducts-all', search],
    queryFn: () => AxProductsApi.getAll({ search, includeInactive: true }),
    enabled: hasToken,
    staleTime: 30_000,
  })

  const filtered = products.filter(
    (p) =>
      (statusFilter === 'all' ||
        p.objectState === statusFilter ||
        (!p.objectState && statusFilter === 'ACTIVE')) &&
      (typeFilter === 'all' || p.type === typeFilter)
  )

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((p) => AxProductsApi.update(p.id, { name: p.name, objectState: state }))
      )
      queryClient.invalidateQueries({ queryKey: ['axproducts-all'] })
      queryClient.invalidateQueries({ queryKey: ['axproducts'] })
      toast.success(t('common.done'), {
        description: t(
          state === 'ACTIVE' ? 'products.doneDetail_activate' : 'products.doneDetail_deactivate',
          { count: selection.length }
        ),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('products.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('products.title')}</h1>
            <p>{t('products.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button onClick={() => navigate(`/admin/products/${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}?new=true`)}>
              <Plus className="size-4" /> {t('products.newProduct')}
            </Button>
          </div>
        </div>

        <ListCard>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Package} />
                {t('products.title')}
              </>
            }
            count={filtered.length}
            controls={
              <>
                <SearchInput
                  value={search}
                  onChange={(v) => {
                    setSearch(v)
                    setSelection([])
                  }}
                />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => {
                    setStatusFilter(v)
                    setSelection([])
                  }}
                  extraActiveCount={typeFilter !== 'all' ? 1 : 0}
                  onReset={() => {
                    setStatusFilter('ACTIVE')
                    setTypeFilter('all')
                    setSelection([])
                  }}
                >
                  <FilterField label={t('common.type')}>
                    <ProductTypeFilter
                      value={typeFilter}
                      onChange={(v) => {
                        setTypeFilter(v)
                        setSelection([])
                      }}
                    />
                  </FilterField>
                </StatusFilterButton>
              </>
            }
          />

          <CardContent className="p-0">
            <BulkActionBar
              count={selection.length}
              onClear={() => setSelection([])}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
            />
            <ProductTable
              products={filtered}
              loading={isLoading}
              emptyMessage={hasToken ? t('products.noProductsFound') : t('common.signInToLoad')}
              selection={selection}
              onSelectionChange={setSelection}
            />
          </CardContent>

          {filtered.length > 0 && (
            <CardFooter>
              <span className="text-xs text-(--ad-text-muted)">
                {t('products.productCount', { count: filtered.length })}
              </span>
            </CardFooter>
          )}
        </ListCard>
      </div>
    </AppShell>
  )
}
